/**
 * EEG Serial Service for Web Serial API connection to Arduino.
 * Kept separate from existing codebase.
 */
class EEGSerialService {
  constructor() {
    this.port = null;
    this.reader = null;
    this.isConnected = false;
    this.isReading = false;
    this.textBuffer = '';
    this.readingsBuffer = [];
    this.onReadingCallback = null;
    this.onConnectionChangeCallback = null;
    this.decoder = new TextDecoder();
    
    // Regex pattern to parse Arduino output: "Delta: X.XX   Theta: X.XX   Alpha: X.XX   Beta: X.XX   Gamma: X.XX"
    this.eegPattern = /Delta:\s*([\d.]+)\s+Theta:\s*([\d.]+)\s+Alpha:\s*([\d.]+)\s+Beta:\s*([\d.]+)\s+Gamma:\s*([\d.]+)/;
  }

  /**
   * Check if Web Serial API is available
   */
  isAvailable() {
    return 'serial' in navigator;
  }

  /**
   * Connect to serial port
   */
  async connect() {
    if (!this.isAvailable()) {
      console.error('[EEG Serial] Web Serial API not available');
      throw new Error('Web Serial API is not available in this browser. Please use Chrome or Edge.');
    }

    try {
      console.log('[EEG Serial] Requesting serial port...');
      // Request port selection from user
      this.port = await navigator.serial.requestPort();
      
      console.log('[EEG Serial] Opening port with baud rate 115200...');
      // Open connection with baud rate 115200
      await this.port.open({ baudRate: 115200 });
      
      this.isConnected = true;
      console.log('[EEG Serial] Port opened successfully. Connection established.');
      this.notifyConnectionChange();
      
      return true;
    } catch (error) {
      if (error.name === 'NotFoundError') {
        throw new Error('No serial port selected');
      } else if (error.name === 'SecurityError') {
        throw new Error('Permission denied to access serial port');
      } else {
        throw new Error(`Connection failed: ${error.message}`);
      }
    }
  }

  /**
   * Disconnect from serial port
   */
  async disconnect() {
    this.stopReading();
    
    if (this.reader) {
      try {
        await this.reader.cancel();
      } catch (error) {
        console.error('Error canceling reader:', error);
      }
      this.reader = null;
    }
    
    if (this.port) {
      try {
        await this.port.close();
      } catch (error) {
        console.error('Error closing port:', error);
      }
      this.port = null;
    }
    
    this.isConnected = false;
    this.notifyConnectionChange();
  }

  /**
   * Start reading data from serial port
   */
  async startReading() {
    if (!this.isConnected || !this.port) {
      console.error('[EEG Serial] Cannot start reading: not connected');
      throw new Error('Not connected to serial port');
    }

    if (this.isReading) {
      console.log('[EEG Serial] Already reading, skipping start');
      return; // Already reading
    }

    try {
      console.log('[EEG Serial] Starting to read from serial port...');
      this.reader = this.port.readable.getReader();
      this.isReading = true;
      this.textBuffer = '';
      console.log('[EEG Serial] Reading loop started. Waiting for data...');

      // Read loop - capture EVERY line
      while (this.isReading) {
        const { value, done } = await this.reader.read();
        
        if (done) {
          console.log('[EEG Serial] Reader done, stopping read loop');
          break;
        }
        
        if (value && value.length > 0) {
          console.log(`[EEG Serial] Received ${value.length} bytes from serial port`);
        }

        // Decode and accumulate text
        this.textBuffer += this.decoder.decode(value, { stream: true });

        // Process complete lines (Arduino outputs one line per reading)
        const lines = this.textBuffer.split('\n');
        this.textBuffer = lines.pop() || ''; // Keep incomplete line in buffer

        // Process each complete line - capture ALL readings
        for (const line of lines) {
          if (line.trim()) {
            const reading = this.parseEEGLine(line.trim());
            if (reading) {
              // Add timestamp
              reading.timestamp = new Date().toISOString();
              
              // Log reading received
              console.log('[EEG Serial] Reading received:', {
                timestamp: reading.timestamp,
                delta: reading.delta,
                theta: reading.theta,
                alpha: reading.alpha,
                beta: reading.beta,
                gamma: reading.gamma,
                bufferSize: this.readingsBuffer.length + 1
              });
              
              // Add to buffer
              this.readingsBuffer.push(reading);
              
              // Notify callback
              if (this.onReadingCallback) {
                this.onReadingCallback(reading);
              }
            } else {
              console.warn('[EEG Serial] Failed to parse line:', line.trim());
            }
          }
        }
      }
    } catch (error) {
      console.error('[EEG Serial] Error reading from serial port:', error);
      this.isReading = false;
      this.isConnected = false;
      this.notifyConnectionChange();
      throw error;
    } finally {
      console.log('[EEG Serial] Reading loop ended');
    }
  }

  /**
   * Stop reading data
   */
  async stopReading() {
    this.isReading = false;
    
    if (this.reader) {
      try {
        await this.reader.cancel();
      } catch (error) {
        console.error('Error canceling reader:', error);
      }
      this.reader = null;
    }
  }

  /**
   * Parse EEG line from Arduino output
   */
  parseEEGLine(line) {
    const match = this.eegPattern.exec(line);
    
    if (match) {
      return {
        delta: parseFloat(match[1]),
        theta: parseFloat(match[2]),
        alpha: parseFloat(match[3]),
        beta: parseFloat(match[4]),
        gamma: parseFloat(match[5]),
        raw_serial_data: line, // Store original for debugging
      };
    }
    
    return null;
  }

  /**
   * Get all buffered readings
   */
  getReadings() {
    const readings = [...this.readingsBuffer];
    console.log(`[EEG Serial] getReadings() called: returning ${readings.length} readings from buffer`);
    return readings;
  }

  /**
   * Clear readings buffer
   */
  clearReadings() {
    const clearedCount = this.readingsBuffer.length;
    this.readingsBuffer = [];
    console.log(`[EEG Serial] clearReadings() called: cleared ${clearedCount} readings from buffer`);
  }

  /**
   * Set callback for new readings
   */
  setOnReading(callback) {
    this.onReadingCallback = callback;
  }

  /**
   * Set callback for connection changes
   */
  setOnConnectionChange(callback) {
    this.onConnectionChangeCallback = callback;
  }

  /**
   * Notify connection change
   */
  notifyConnectionChange() {
    if (this.onConnectionChangeCallback) {
      this.onConnectionChangeCallback(this.isConnected);
    }
  }
}

// Export singleton instance
export default new EEGSerialService();
